/**
 * useSetMeetingVisibility — mutation to change a meeting's visibility.
 *
 * Wraps PUT /api/orgs/<orgId>/meetings/<meetingEntityId>/visibility/ and, on
 * success, refetches the meeting object so the store (and thus the badge)
 * reflects the server-derived access_summary without a page reload.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchCrmObjectDetail } from '@/lib/crm/query/queries';
import { apiPut } from '@/lib/http/client';
import { logger } from '@/lib/logger';

const VISIBILITY_REQUEST_TIMEOUT_MS = 10000;

export function useSetMeetingVisibility(
  orgId: number,
  meetingEntityId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (visibility: 'private' | 'org') =>
      apiPut<{ visibility: string }>(
        `/api/orgs/${orgId}/meetings/${meetingEntityId}/visibility/`,
        { visibility },
        VISIBILITY_REQUEST_TIMEOUT_MS
      ),
    onSuccess: async () => {
      try {
        await fetchCrmObjectDetail(
          queryClient,
          orgId,
          'ahoy_meeting',
          meetingEntityId
        );
      } catch (err) {
        logger.warn('meeting visibility refetch failed', { err });
      }
    },
  });
}
